import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { LanguageProvider, type Lang } from "@/lib/i18n";
import CookieConsent from "@/components/CookieConsent";
import BreakingNewsBar from "@/components/BreakingNewsBar";
import { SEO_I18N, HREFLANG, OG_LOCALE, ALL_LANGS } from "@/lib/seo-i18n";

import appCss from "../styles.css?url";

const ADSENSE_CLIENT = (import.meta.env.VITE_ADSENSE_CLIENT as string | undefined) ?? "ca-pub-9362953386244939";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  validateSearch: (search: Record<string, unknown>) => {
    const raw = typeof search.lang === "string" ? search.lang : undefined;
    const lang = raw && (ALL_LANGS as string[]).includes(raw) ? (raw as Lang) : undefined;
    return { lang };
  },
  loaderDeps: ({ search }) => ({ lang: search.lang ?? "en" }),
  loader: ({ deps }) => ({ lang: deps.lang as Lang }),
  head: ({ loaderData }) => {
    const SITE = "https://hantavirus.international";
    const lang: Lang = (loaderData?.lang as Lang | undefined) ?? "en";
    const seo = SEO_I18N[lang];
    const TITLE = seo.title;
    const DESC = seo.description;
    const KEYWORDS = seo.keywords;
    
    const canonical = lang === "en" ? SITE : `${SITE}/?lang=${lang}`;
    const jsonLd = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          name: "Hantavirus International",
          url: SITE,
          inLanguage: ALL_LANGS.map((l) => HREFLANG[l]),
          potentialAction: {
            "@type": "SearchAction",
            target: `${SITE}/?q={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        },
        {
          "@type": "Organization",
          name: "Hantavirus International",
          url: SITE,
            logo: `${SITE}/favicon-192x192.png`,
        },
        {
          "@type": "MedicalWebPage",
          name: TITLE,
          description: DESC,
          url: canonical,
          inLanguage: HREFLANG[lang],
          about: {
            "@type": "MedicalCondition",
            name: "Hantavirus infection",
            alternateName: ["HPS", "HCPS", "HFRS", "Hantavirus Pulmonary Syndrome", "Andes virus"],
            code: { "@type": "MedicalCode", codingSystem: "ICD-10", code: "B33.4" },
          },
          audience: { "@type": "Audience", audienceType: "Public health, clinicians, travellers" },
          lastReviewed: new Date().toISOString().slice(0, 10),
          
          publisher: {
            "@type": "Organization",
            name: "Hantavirus International",
            url: SITE,
            logo: { "@type": "ImageObject", url: `${SITE}/favicon-192x192.png` },
          },
        },
        {
          "@type": "Dataset",
          name: "Global Hantavirus Outbreak Cases",
          description:
            "Live, continuously-updated dataset of confirmed, suspected and fatal hantavirus cases worldwide, with onset dates, locations, variants (ANDV, SNV, HTNV, SEOV, PUUV) and source links to WHO, CDC, UKHSA, ECDC and national health ministries.",
          url: SITE,
          keywords: ["hantavirus", "ANDV", "Andes virus", "Sin Nombre", "HFRS", "HPS", "outbreak", "epidemiology"],
          isAccessibleForFree: true,
          license: "https://creativecommons.org/licenses/by/4.0/",
          creator: {
            "@type": "Organization",
            name: "Hantavirus International",
            url: SITE,
          },
          spatialCoverage: { "@type": "Place", name: "Worldwide" },
          temporalCoverage: `2024-01-01/${new Date().toISOString().slice(0, 10)}`,
          distribution: [
            { "@type": "DataDownload", encodingFormat: "text/html", contentUrl: SITE },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What is hantavirus?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Hantaviruses are a family of rodent-borne viruses that cause two serious human diseases: Hantavirus Pulmonary Syndrome (HPS/HCPS) in the Americas, primarily caused by Sin Nombre virus and Andes virus, and Hemorrhagic Fever with Renal Syndrome (HFRS) in Eurasia, caused by Hantaan, Seoul, Puumala and Dobrava viruses.",
              },
            },
            {
              "@type": "Question",
              name: "How is hantavirus transmitted?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Most hantaviruses spread to humans through inhalation of aerosolized rodent urine, droppings or saliva, or by direct contact with infected rodents. Andes virus (ANDV) is the only hantavirus with documented person-to-person transmission, mainly during the prodromal and cardiopulmonary phases.",
              },
            },
            {
              "@type": "Question",
              name: "What are the symptoms of hantavirus infection?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Early symptoms include fever, fatigue, severe muscle aches, headache, dizziness, chills and abdominal symptoms. HPS progresses to coughing, shortness of breath and pulmonary edema. HFRS causes acute kidney injury, low blood pressure and hemorrhage. Both diseases can be fatal — case fatality reaches 35–40% for HPS.",
              },
            },
            {
              "@type": "Question",
              name: "Is there a vaccine or treatment for hantavirus?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "There is no specific antiviral therapy or widely-licensed vaccine for hantavirus infection. Treatment is supportive: oxygen, mechanical ventilation, ECMO for severe HPS, and dialysis for HFRS. Early intensive-care admission improves survival significantly.",
              },
            },
            {
              "@type": "Question",
              name: "How can I prevent hantavirus infection?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Reduce rodent contact: seal entry points to homes and cabins, store food in rodent-proof containers, ventilate enclosed spaces before cleaning, wet-clean droppings (never sweep or vacuum dry), wear N95 respirators and gloves when cleaning rodent-contaminated areas, and avoid handling live or dead rodents directly.",
              },
            },
          ],
        },
      ],
    };

    const altLocaleMeta = ALL_LANGS
      .filter((l) => l !== lang)
      .map((l) => ({ property: "og:locale:alternate", content: OG_LOCALE[l] }));

    const hreflangLinks = [
      ...ALL_LANGS.map((l) => ({
        rel: "alternate",
        hrefLang: HREFLANG[l],
        href: l === "en" ? SITE : `${SITE}/?lang=${l}`,
      })),
      { rel: "alternate", hrefLang: "x-default", href: SITE },
    ];

    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { title: TITLE },
        { name: "description", content: DESC },
        { name: "keywords", content: KEYWORDS },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
        { name: "googlebot", content: "index, follow" },
        { name: "author", content: "Hantavirus International" },
        { name: "theme-color", content: "#b91c1c" },
        { property: "og:site_name", content: "Hantavirus International" },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESC },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: OG_LOCALE[lang] },
        ...altLocaleMeta,
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESC },
      ],
      links: [
        // Canonical is emitted per-route via buildSeo() to avoid duplicate
        // <link rel="canonical"> tags (Bing/SEO checker flags multiples).
        ...hreflangLinks,
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" as const },
        { rel: "preconnect", href: "https://geukzrrwatosjruicwgh.supabase.co", crossOrigin: "anonymous" as const },
        { rel: "preconnect", href: "https://a.basemaps.cartocdn.com", crossOrigin: "anonymous" as const },
        { rel: "preconnect", href: "https://b.basemaps.cartocdn.com", crossOrigin: "anonymous" as const },
        { rel: "preconnect", href: "https://c.basemaps.cartocdn.com", crossOrigin: "anonymous" as const },
        { rel: "preconnect", href: "https://d.basemaps.cartocdn.com", crossOrigin: "anonymous" as const },
        // Async-load Google Fonts CSS to avoid render-blocking. media="print" makes the browser
        // fetch it without blocking render; an inline script flips it to "all" once loaded.
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;700&family=Inter:wght@400;500;600;700&display=swap",
          media: "print",
          onLoad: "this.media='all';this.onload=null;",
        } as any,
        { rel: "stylesheet", href: appCss },
        { rel: "icon", href: "/favicon.ico?v=5", sizes: "any" },
        { rel: "icon", type: "image/png", href: "/favicon-32x32.png?v=5", sizes: "32x32" },
        { rel: "icon", type: "image/png", href: "/favicon-192x192.png?v=5", sizes: "192x192" },
        { rel: "apple-touch-icon", href: "/apple-touch-icon.png?v=5", sizes: "180x180" },
        { rel: "manifest", href: "/site.webmanifest?v=5" },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(jsonLd) },
        ...(ADSENSE_CLIENT
          ? [
              {
                children: `(function(){function l(){if(window.__adsLoaded)return;window.__adsLoaded=true;var s=document.createElement('script');s.src='https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}';s.async=true;s.crossOrigin='anonymous';document.head.appendChild(s);}function d(){if('requestIdleCallback' in window){requestIdleCallback(l,{timeout:8000});}else{setTimeout(l,5000);}}if(document.readyState==='complete'){setTimeout(d,4000);}else{window.addEventListener('load',function(){setTimeout(d,4000);},{once:true});}['scroll','mousemove','touchstart','keydown'].forEach(function(e){window.addEventListener(e,l,{once:true,passive:true});});})();`,
              },
            ]
          : []),
      ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <BreakingNewsBar />
        <Outlet />
        <CookieConsent />
      </LanguageProvider>
    </QueryClientProvider>
  );
}
